import { 
    Stack,
    Box,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    CircularProgress,
} from "@mui/material";
import { 
    DataGrid, 
    GridColDef, 
    GridRowSelectionModel,
    GridRenderCellParams,
} from "@mui/x-data-grid";
import React, { useEffect, useState } from "react";
import DeleteIcon from '@mui/icons-material/Delete';
import InfoIcon from '@mui/icons-material/Info';
import { useNavigate } from "react-router";
import { useApiService } from "../../../../../services/ApiService/ApiService";

interface IceVehicle {
    id: string;
    uuid: string;
    registrationNumber: string;
    make: string;
    model: string;
    journeyCount: number;
    selected: boolean;
}

interface IceVehicleTableProps {
    allVehicles: [
        IceVehicle[],
        React.Dispatch<React.SetStateAction<IceVehicle[]>>
    ];
    userInfo: {
        uuid: string;
        fleetUuid: string;
    };
    loading?: boolean;
}

export default function IceVehicleTable(props: IceVehicleTableProps) {
    const [allVehicles, setAllVehicles] = props.allVehicles;
    const userInfo = props.userInfo;
    const { fetchWithAuth } = useApiService();
    const loading = props.loading || false;

    const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>([]);
    const [selectedRow, setSelectedRow] = useState<IceVehicle[]>([]);
    const [modalDeleteEvent, setModalDeleteEvent] = useState<{row: IceVehicle; id: string} | null>(null);
    const [modalIsOpen, setIsOpen] = useState(false);
    const [isError, setIsError] = useState(false);
    const navigate = useNavigate();

    function openModal(params: GridRenderCellParams<any, IceVehicle>) {
        setModalDeleteEvent({
            row: params.row,
            id: params.row.id
        });
        setIsOpen(true);
    }

    function closeModal() {
        setIsOpen(false);
    }

    const columnHelpContent = [
        { title: "VRN", description: "This is the description for column 1" },
        { title: "Model", description: "This is the description for column 2" },
        { title: "Journey Counts", description: "This is the description for column 3" },
    ];
    // HelpAction component to render the help action menu
    const HelpAction = ({ title, description }: any) => {
        const [show, setShow] = useState(false);

        const handleShow = () => setShow(true);
        const handleHide = () => setShow(false);

        return (
            <>
                {/* <Button
                    variant="link"
                    onClick={handleShow}>
                    <i className="fa fa-question-circle"></i>
                </Button> */}

                {/* <Popover show={show}>
                    <Popover.Header>
                        <Popover.Header>{title}</Popover.Header>
                    </Popover.Header>
                    <Popover.Body>{description}</Popover.Body>
                </Popover> */}
            </>
        );
    };

    // Custom column header component with the HelpAction component
    const CustomHeader = ({ column }: any) => {
        const { title, description } = columnHelpContent.find((c) => c.title === column.name) as any;

        return (
            <>
                {column.name}
                <HelpAction
                    title={title}
                    description={description}
                />
            </>
        );
    };

    function toggleIceVehicle(iceVehicleUuid: string) {
        let bodyData: { [name: string]: string } = {};
        bodyData["uuid"] = userInfo.uuid;
        bodyData["fleetUuid"] = userInfo.fleetUuid;
        bodyData["iceVehicleUuid"] = iceVehicleUuid;

        fetchWithAuth('/DragDrop/fleet/scenario/ice-vehicles/toggle', {
            method: "POST",
            body: JSON.stringify(bodyData),
        }).then((res) => {
            if (res.status !== 200) {
                setIsError(true);
                return;
            }
            res.json().then((res_obj) => { });
        });
    }

    function deleteVehicle(iceVehicle: IceVehicle | undefined) {
        if (!iceVehicle) return;
        
        let bodyData: { [name: string]: any } = {};
        bodyData["uuid"] = userInfo.uuid;
        bodyData["fleetUuid"] = userInfo.fleetUuid;
        bodyData["iceVehicle"] = iceVehicle;

        try {
            fetchWithAuth('/DragDrop/fleet/icevehicle/delete', {
                method: "POST",
                body: JSON.stringify(bodyData),
            })
                .then(response => {
                    if (!response.ok) {
                        throw new Error('Network response was not ok');
                    }
                })
                .catch(() => {
                    setIsError(true);
                });
        } catch {
            setIsError(true);
        }
    }

    function removeRows() {
        if (!modalDeleteEvent) return;
        deleteVehicle(modalDeleteEvent.row);
        setAllVehicles((prev: IceVehicle[]) => prev.filter((e: IceVehicle) => e.id !== modalDeleteEvent.id));
        closeModal();
    }

    function toggleAll(selected: boolean) {
        let bodyData: { [name: string]: any } = {};
        bodyData["uuid"] = userInfo.uuid;
        bodyData["fleetUuid"] = userInfo.fleetUuid;
        bodyData["allSelected"] = selected;
        
        fetchWithAuth('/DragDrop/fleet/scenario/ice-vehicles/toggle/all', {
            method: "POST",
            body: JSON.stringify(bodyData),
        }).then((res) => {
            if (res.status !== 200) {
                setIsError(true);
                return;
            }
            res.json().then((res_obj) => { });
        });
    }

    //Function used for navigate to insight page
    function navigateScreen(value: any, path: any) {
        switch (path) {
            case "/plan/insight/fleet/scenario/categories/vehicle-insight": {
                navigate(path, {
                    state: {
                        iceVehicle: value.row,
                    },
                });
                return;
            }
            case "/plan/insight/fleet/scenario/categories": {
                navigate(path);
                return;
            }
            default: {
                //statements;
                break;
            }
        }
    }

    useEffect(() => {
        const initialSelectedRow = allVehicles.filter((e) => e.selected);
        setSelectionModel(initialSelectedRow.map((r) => r.id));
        setSelectedRow(initialSelectedRow);
    }, [allVehicles]);

    const handleSelectionChange = (newSelectionModel: GridRowSelectionModel) => {
        setSelectionModel(newSelectionModel);
        const selectedIDs = new Set(newSelectionModel);
        const selectedRowData = allVehicles.filter((row) => selectedIDs.has(row.id));
        
        if (newSelectionModel.length === allVehicles.length) {
            toggleAll(true);
        } else if (newSelectionModel.length === 0) {
            toggleAll(false);
        } else {
            const rowChanges = selectedRowData.filter(x => !selectedRow.includes(x))
                .concat(selectedRow.filter(x => !selectedRowData.includes(x)));
            
            rowChanges.forEach(row => {
                toggleIceVehicle(row.id);
            });
        }
        
        setSelectedRow(selectedRowData);
    };

    const columns: GridColDef[] = [
        {
            field: "registrationNumber",
            headerName: "VRN",
            sortable: true,
            filterable: true,
            width: 150,
        },
        {
            field: "make",
            headerName: "Make",
            sortable: false,
            width: 130,

        },
        {
            field: "model",
            headerName: "Model",
            sortable: false,
            width: 240,

        },
        {
            field: "journeyCount",
            headerName: "Journey Counts",
            sortable: false,
            width: 130,
            align: 'right',
            headerAlign: 'right',

        },
        {
            field: "actions",
            headerName: "Actions",
            sortable: false,
            width: 100,
            renderHeader: () => (
                <Typography>Actions</Typography>
            ),
            renderCell: (params: GridRenderCellParams<any, IceVehicle>) => (
                <Stack direction="row" spacing={1}>
                    <IconButton
                        size="small"
                        onClick={() => openModal(params)}
                        color="error"
                    >
                        <DeleteIcon fontSize="small" />
                    </IconButton>
                </Stack>
            ),
        },
    ];

    return (
        <>
            {loading ? (
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                    <CircularProgress />
                </Box>
            ) : (
                <DataGrid
                    rows={allVehicles}
                    columns={columns}
                    checkboxSelection
                    disableRowSelectionOnClick
                    disableColumnFilter
                    disableColumnMenu
                    disableColumnSelector
                    disableDensitySelector
                    onRowSelectionModelChange={handleSelectionChange}
                    rowSelectionModel={selectionModel}
                    initialState={{
                        pagination: {
                            paginationModel: { pageSize: 10 },
                        },
                    }}
                    pageSizeOptions={[10, 25, 50]}
                    sx={{
                        '& .MuiDataGrid-columnHeaders': {
                            backgroundColor: 'background.paper',
                            borderBottom: 1,
                            borderColor: 'divider',
                        },
                        '& .MuiDataGrid-cell': {
                            borderBottom: 1,
                            borderColor: 'divider',
                        },
                        '& .MuiDataGrid-row:hover': {
                            backgroundColor: 'action.hover',
                        },
                    }}
                />
            )}
        </>
    );
}